import React from 'react';
import {
  CalendarClockIcons,
  EmailIcons,
  GitHubIcons,
  LinkIcons,
  LinkedInIcons,
  MapPinIcons,
  PhoneIcons,
  WebsiteIcons,
} from '../../../../../icons/icons';
import { Address, SocialLinks, StatusHistoryItem } from '../../../../../utils/types/JobOpenings';
import { StatusHistoryTimeline } from '../../../../../ui/components/Timeline';

interface IOverview {
  phone?: string;
  email?: string;
  address?: Address;
  aiScore?: number;
  applicantStatus?: string;
  notes?: string;
  socials?: SocialLinks;
  statusHistory?: StatusHistoryItem[];
  createdAt?: number | string;
}
export const OverviewTab = (props: IOverview) => {
  const { phone, email, address, applicantStatus, aiScore, notes, socials, statusHistory, createdAt } = props;
  return (
    <div className='space-y-6 py-2 animate-fadeIn'>
      { /* Contact Info */ }
      <section className='bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden'>
        <div className='bg-blue-50 px-4 py-3 border-b border-gray-100'>
          <h3 className='text-sm font-semibold text-blue-700 flex items-center'>
            <PhoneIcons className='h-4 w-4 mr-2' />
            Contact Information
          </h3>
        </div>
        <div className='divide-y divide-gray-100'>
          <div className='px-4 py-3 flex items-start'>
            <div className='flex-shrink-0 text-blue-500 mr-3 mt-1'>
              <PhoneIcons className='h-5 w-5' />
            </div>
            <div>
              <p className='text-xs font-medium text-gray-500 mb-1'>Phone</p>
              <p className='text-[13px] font-medium text-gray-800'>{ phone }</p>
            </div>
          </div>

          <div className='px-4 py-3 flex items-start'>
            <div className='flex-shrink-0 text-blue-500 mr-3 mt-1'>
              <EmailIcons className='h-5 w-5' />
            </div>
            <div>
              <p className='text-xs font-medium text-gray-500 mb-1'>Email</p>
              <p className='text-[13px] font-medium text-gray-800 break-all'>
                { email }
              </p>
            </div>
          </div>

          { address && (
            <div className='px-4 py-3 flex items-start'>
              <div className='flex-shrink-0 text-blue-500 mr-3 mt-1'>
                <MapPinIcons className='h-5 w-5' />
              </div>
              <div>
                <p className='text-xs font-medium text-gray-500 mb-1'>
                  Address
                </p>
                <p className='text-[13px] font-medium text-gray-800'>
                  { [
                    address.street,
                    address.city,
                    address.state,
                    address.country,
                  ]
                    .filter(Boolean)
                    .join(', ') }
                </p>
              </div>
            </div>
          ) }
        </div>
      </section>

      { /* Status */ }
      <section className='bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden'>
        <div className='bg-green-50 px-4 py-3 border-b border-gray-100'>
          <h3 className='text-sm font-semibold text-green-700 flex items-center'>
            <CalendarClockIcons className='h-4 w-4 mr-2' />
            Application Status
          </h3>
        </div>
        <div className='p-4'>
          <div className='flex flex-wrap gap-3'>
            <div
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                  applicantStatus === 'Outstanding'
                    ? 'bg-blue-100 text-blue-800'
                    : applicantStatus === 'Auto-Assessment'
                      ? 'bg-purple-100 text-purple-800'
                      : applicantStatus === 'Shortlisted'
                        ? 'bg-green-100 text-green-800'
                        : applicantStatus === 'Unsuccessful'
                          ? 'bg-red-100 text-red-800'
                          : applicantStatus === 'Suitable'
                            ? 'bg-teal-100 text-teal-800'
                            : applicantStatus === 'New'
                              ? 'bg-yellow-100 text-yellow-800'
                              : applicantStatus === 'Completed'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-gray-100 text-gray-800'
                }`}
            >
              <span
                  className={`w-2 h-2 mr-1.5 rounded-full ${
                    applicantStatus === 'Outstanding'
                      ? 'bg-blue-500'
                      : applicantStatus === 'Auto-Assessment'
                        ? 'bg-purple-500'
                        : applicantStatus === 'Shortlisted'
                          ? 'bg-green-500'
                          : applicantStatus === 'Unsuccessful'
                            ? 'bg-red-500'
                            : applicantStatus === 'Suitable'
                              ? 'bg-teal-500'
                              : applicantStatus === 'New'
                                ? 'bg-yellow-500'
                                : applicantStatus === 'Completed'
                                  ? 'bg-emerald-500'
                                  : 'bg-gray-500'
                  }`}
              ></span>
              { applicantStatus }
            </div>

            { aiScore && (
              <div className='inline-flex items-center px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium'>
                <LinkIcons className='h-3.5 w-3.5 mr-1.5' />
                CV Score: { aiScore }%
              </div>
            ) }
          </div>
        </div>
      </section>

      { /* Status History Timeline */ }
      <StatusHistoryTimeline
        statusHistory={statusHistory}
        currentStatus={applicantStatus}
        createdAt={createdAt}
        title='Application Timeline'
      />

      { socials &&
        (socials.github || socials.linkedin || socials.personalWebsite) && (
        <section className='bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden'>
          <div className='bg-purple-50 px-4 py-3 border-b border-gray-100'>
            <h3 className='text-sm font-semibold text-purple-700 flex items-center'>
              <LinkIcons className='h-4 w-4 mr-2' />
              Social Links
            </h3>
          </div>
          <div className='p-4'>
            <div className='flex flex-wrap justify-start gap-4'>
              { socials?.github && (
                <a
                    href={
                      socials.github.startsWith('http')
                        ? socials.github
                        : `https://${socials.github}`
                    }
                    target='_blank'
                    rel='noopener noreferrer'
                    title='GitHub'
                    className='flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-all duration-200'
                >
                  <GitHubIcons className='w-5 h-5 mr-2' />
                  <span className='text-xs font-medium'>GitHub</span>
                </a>
              ) }

              { socials?.linkedin && (
                <a
                    href={
                      socials.linkedin.startsWith('http')
                        ? socials.linkedin
                        : `https://${socials.linkedin}`
                    }
                    target='_blank'
                    rel='noopener noreferrer'
                    title='LinkedIn'
                    className='flex items-center px-3 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200'
                >
                  <LinkedInIcons className='w-5 h-5 mr-2' />
                  <span className='text-xs font-medium'>LinkedIn</span>
                </a>
              ) }

              { socials?.personalWebsite && (
                <a
                    href={
                      socials.personalWebsite.startsWith('http')
                        ? socials.personalWebsite
                        : `https://${socials.personalWebsite}`
                    }
                    target='_blank'
                    rel='noopener noreferrer'
                    title='Website'
                    className='flex items-center px-3 py-2 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg transition-all duration-200'
                >
                  <WebsiteIcons className='w-5 h-5 mr-2' />{ ' ' }
                  <span className='text-xs font-medium'>Website</span>
                </a>
              ) }
            </div>
          </div>
        </section>
      ) }

      { /* Social Links */ }
    </div>
  );
};
